// Real in-canvas texture painting for FX & Materials (docs/3d-content-
// pipeline/roadmap.md 3.2 Phase 3 / section 6). Pure, DOM-free functions
// over a plain Uint8ClampedArray, same discipline as textureGenerators.ts
// (this repo's test environment has no real canvas 2D context - see that
// file's own header comment) - the caller (fxMaterialPaintSession.ts) owns
// the actual `<canvas>`/`CanvasTexture` and only ever hands pixel data in
// and out of these functions.
//
// The paint target for v1 is FX & Materials' preview sphere: a standard UV
// sphere, U = longitude (a seam at u=0/1, the texture wraps here) and
// V = latitude (a pinch at v=0 and v=1, poles do not wrap). Every function
// below that deals with the U axis has to treat it as a torus, not a plain
// 0..1 range - that is the one property that makes this different from
// textureGenerators.ts's own tiling noise field.
import { hexToRgb } from './textureGenerators';

/** UV -> canvas-pixel convention used throughout this module: column
 * `x = u * width`, row `y = (1 - v) * height`. This is the same top-down
 * convention every uploaded/generated texture on this sphere already
 * renders correctly under (drei's `useTexture` + three.js's default
 * `flipY = true`) - painting uses the identical mapping so a painted stroke
 * lands exactly where the cursor sits, with no separate flip anywhere in
 * this feature. */
function uvToPixel(u: number, v: number, width: number, height: number): { x: number; y: number } {
  return { x: u * width, y: (1 - v) * height };
}

/**
 * Stamps a soft round brush centred at UV (`u`, `v`) directly into `pixels`
 * (mutated in place - deliberately, unlike every generator in
 * textureGenerators.ts, which always returns a fresh array: a 512x512x4
 * buffer is 1 MiB, and a stroke calls this dozens of times a second, so
 * reallocating per stamp would be wasteful for no benefit here).
 *
 * `hardness` (0..1) sets how much of the brush radius stays at full
 * strength before falling off linearly to zero at the edge: 0 is a fully
 * soft brush (falloff starts at the centre), 1 is a hard-edged disc.
 *
 * The brush's own footprint wraps across the sphere's U seam (a torus, not
 * a clamp): a stamp whose circle crosses x=0 or x=width also paints the
 * wrapped-around columns on the opposite edge, so painting directly on the
 * seam does not leave one side of it untouched. `texture.wrapS` must be set
 * to `THREE.RepeatWrapping` by the caller for the GPU to then sample that
 * wrapped edge as continuous. The V axis is never wrapped - a sphere's
 * poles pinch, they do not wrap - so a stamp is simply clipped at the top
 * and bottom of the canvas.
 */
export function stampBrush(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  u: number,
  v: number,
  radiusPx: number,
  hardness: number,
  colourRgb: [number, number, number],
  opacity: number,
): void {
  const { x: cx, y: cy } = uvToPixel(u, v, width, height);
  const coreRadius = Math.min(0.999, Math.max(0, hardness));
  const [cr, cg, cb] = colourRgb;
  const minY = Math.max(0, Math.floor(cy - radiusPx));
  const maxY = Math.min(height - 1, Math.ceil(cy + radiusPx));
  const minX = Math.floor(cx - radiusPx);
  const maxX = Math.ceil(cx + radiusPx);

  for (let y = minY; y <= maxY; y++) {
    for (let rawX = minX; rawX <= maxX; rawX++) {
      const dx = rawX + 0.5 - cx;
      const dy = y + 0.5 - cy;
      const t = Math.sqrt(dx * dx + dy * dy) / radiusPx;
      if (t > 1) continue;
      const falloff = t <= coreRadius ? 1 : 1 - (t - coreRadius) / (1 - coreRadius);
      const srcA = Math.max(0, Math.min(1, falloff)) * opacity;
      if (srcA <= 0) continue;

      const x = ((Math.floor(rawX) % width) + width) % width;
      const i = (y * width + x) * 4;
      const dstA = pixels[i + 3]! / 255;
      const outA = srcA + dstA * (1 - srcA);
      if (outA <= 0) continue;
      pixels[i] = (cr * srcA + pixels[i]! * dstA * (1 - srcA)) / outA;
      pixels[i + 1] = (cg * srcA + pixels[i + 1]! * dstA * (1 - srcA)) / outA;
      pixels[i + 2] = (cb * srcA + pixels[i + 2]! * dstA * (1 - srcA)) / outA;
      pixels[i + 3] = outA * 255;
    }
  }
}

/**
 * Fills the gap between two consecutive stroke samples (`u1,v1` to `u2,v2`)
 * with evenly-spaced intermediate points, roughly `spacingPx` apart, so a
 * fast pointer move does not leave visible gaps between brush stamps - the
 * same problem every paint tool has, since `pointermove` fires far less
 * often than a brush needs stamps at typical cursor speeds.
 *
 * Handles the sphere's U seam: if the two points are more than half the
 * texture apart in U, the cursor did not actually travel across the middle
 * of the texture, it crossed the seam - the short way around - so the
 * interpolation follows that short path instead of a naive lerp, which
 * would otherwise paint a long, bogus diagonal stripe across the texture.
 * Returned `u` values are wrapped back into [0, 1).
 *
 * Deliberately does not attempt to correct for pole-pinch distortion (large
 * on-texture jumps in `u` from a small cursor movement near v=0/v=1) - an
 * inherent property of sphere-UV projection painting that every reference
 * tool shares, out of scope for v1.
 *
 * `size` is the paint canvas's pixel width, used only to convert a UV
 * distance into an approximate pixel distance for spacing purposes; this
 * assumes a square canvas (true of every paint session this feature
 * creates today).
 */
export function interpolateStrokePoints(
  u1: number,
  v1: number,
  u2: number,
  v2: number,
  spacingPx: number,
  size: number,
): Array<{ u: number; v: number }> {
  let du = u2 - u1;
  if (Math.abs(du) > 0.5) du += du > 0 ? -1 : 1;
  const dv = v2 - v1;

  const distPx = Math.sqrt((du * size) ** 2 + (dv * size) ** 2);
  const steps = Math.max(1, Math.ceil(distPx / Math.max(1, spacingPx)));

  const points: Array<{ u: number; v: number }> = [];
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const u = ((u1 + du * t) % 1 + 1) % 1;
    const v = v1 + dv * t;
    points.push({ u, v });
  }
  return points;
}

/**
 * Bleeds each fully-painted pixel's colour one texel into any adjacent
 * fully-unpainted (alpha < 255) pixel, `iterations` times - the standard
 * defence against a filtered/mipmapped texture sampling into an unpainted
 * region and showing a dark or transparent fringe at a paint boundary.
 *
 * In this feature's actual v1 usage the canvas is always seeded fully
 * opaque (see `fillSolidColour` below) and `stampBrush` always blends onto
 * an opaque destination, so every pixel's alpha stays 255 and this pass has
 * nothing to do today - the sphere's own seam is instead handled at the
 * source by `stampBrush`'s own U-axis wrapping, not by dilating afterwards.
 * It is still run once before every save (cheap: an early per-pixel alpha
 * check) because it is genuinely correct, generically useful infrastructure
 * (documented here rather than deleted) for the moment this feature grows
 * an eraser or any other path that can leave a pixel partially unpainted.
 */
export function dilateTexture(pixels: Uint8ClampedArray, width: number, height: number, iterations: number): void {
  let current = pixels;
  for (let iter = 0; iter < iterations; iter++) {
    const next = Uint8ClampedArray.from(current);
    let changed = false;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        if (current[i + 3] === 255) continue;
        let sumR = 0, sumG = 0, sumB = 0, count = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = ((x + dx) % width + width) % width;
            const ny = y + dy;
            if (ny < 0 || ny >= height) continue;
            const ni = (ny * width + nx) * 4;
            if (current[ni + 3] !== 255) continue;
            sumR += current[ni]!; sumG += current[ni + 1]!; sumB += current[ni + 2]!;
            count++;
          }
        }
        if (count > 0) {
          next[i] = sumR / count; next[i + 1] = sumG / count; next[i + 2] = sumB / count; next[i + 3] = 255;
          changed = true;
        }
      }
    }
    current = next;
    if (!changed) break;
  }
  pixels.set(current);
}

/** Fills the whole buffer with one opaque colour - used to seed a paint
 * session's canvas when the material has no existing albedo map yet. A
 * `meshStandardMaterial` with no `transparent`/`alphaTest` set (true of
 * both FX preview spheres) multiplies `color` by the texture's RGB only,
 * regardless of the texture's own alpha - so an unseeded, all-transparent
 * canvas would render the whole sphere solid black the instant Paint mode
 * is entered, before a single stroke. Seeding opaque with the material's
 * current colour makes entering-then-leaving Paint mode a visual no-op. */
export function fillSolidColour(pixels: Uint8ClampedArray, width: number, height: number, colourHex: string): void {
  const [r, g, b] = hexToRgb(colourHex);
  for (let i = 0; i < width * height * 4; i += 4) {
    pixels[i] = r; pixels[i + 1] = g; pixels[i + 2] = b; pixels[i + 3] = 255;
  }
}
